import { useEffect, useCallback, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { 
  hasConsent, 
  trackPageForRetargeting, 
  trackClickForRetargeting,
  updateRetargetingData,
  updateEngagementLevel,
  incrementSessionCount,
  getRetargetingData
} from '@/lib/cookieConsent';

// Check if we're on production domain
const isProductionDomain = (): boolean => {
  if (typeof window === 'undefined') return false;
  const hostname = window.location.hostname;
  return hostname === 'xpandagent.io' || hostname === 'xpansion-core.lovable.app';
};

export function useCookieTracking() {
  const location = useLocation();
  const sessionStartTime = useRef<number>(Date.now());
  const pageViews = useRef<number>(0);
  const clicks = useRef<number>(0);
  const isNewSession = useRef<boolean>(true);

  // Check if tracking is allowed
  const canTrack = useCallback(() => {
    return isProductionDomain() && hasConsent('marketing');
  }, []);

  // Initialize session on mount
  useEffect(() => {
    if (!canTrack()) return;

    // Check if this is a new session
    const lastActivity = sessionStorage.getItem('xpand_cookie_last_activity');
    const thirtyMinutesAgo = Date.now() - (30 * 60 * 1000);
    
    if (!lastActivity || parseInt(lastActivity) < thirtyMinutesAgo) {
      isNewSession.current = true;
      incrementSessionCount();
    } else {
      isNewSession.current = false;
    }

    // Get UTM params from URL
    const params = new URLSearchParams(window.location.search);
    const utmSource = params.get('utm_source');
    const utmMedium = params.get('utm_medium');
    const utmCampaign = params.get('utm_campaign');

    if (utmSource || utmMedium || utmCampaign) {
      updateRetargetingData({
        utmSource,
        utmMedium,
        utmCampaign,
      });
    }
  }, [canTrack]);

  // Track page views
  useEffect(() => {
    if (!canTrack()) return;

    pageViews.current += 1;
    trackPageForRetargeting(location.pathname);
    
    // Update last activity
    sessionStorage.setItem('xpand_cookie_last_activity', Date.now().toString());
  }, [location.pathname, canTrack]);

  // Track clicks for retargeting
  useEffect(() => {
    if (!canTrack()) return;

    const handleClick = (e: MouseEvent) => {
      clicks.current += 1;
      
      const target = e.target as HTMLElement;
      const clickableText = target.innerText?.trim().substring(0, 50);
      
      // Track CTA clicks specifically
      if (
        target.tagName === 'BUTTON' ||
        target.tagName === 'A' ||
        target.closest('button') ||
        target.closest('a')
      ) {
        if (clickableText) {
          trackClickForRetargeting(clickableText);
        }
      }
    };

    document.addEventListener('click', handleClick);
    return () => document.removeEventListener('click', handleClick);
  }, [canTrack]);

  // Update engagement level periodically
  useEffect(() => {
    if (!canTrack()) return;

    const updateEngagement = () => {
      const timeOnSite = Math.floor((Date.now() - sessionStartTime.current) / 1000);
      const level = updateEngagementLevel(pageViews.current, timeOnSite, clicks.current);
      
      updateRetargetingData({
        engagementLevel: level,
        averageTimeOnSite: timeOnSite,
      });
    };

    // Update every 30 seconds
    const interval = setInterval(updateEngagement, 30000);

    // Update on page unload
    const handleUnload = () => {
      updateEngagement();
    };

    window.addEventListener('beforeunload', handleUnload);

    return () => {
      clearInterval(interval);
      window.removeEventListener('beforeunload', handleUnload);
    };
  }, [canTrack]);

  return {
    canTrack: canTrack(),
    retargetingData: getRetargetingData(),
  };
}
